/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';
import app from 'flarum/forum/app';
import MobileTourStepModal, { type MobileTourStepData } from './MobileTourStepModal';
import type Mithril from 'mithril';

export interface MobileTourCardAttrs extends ComponentAttrs {
	cardId: string;
	complete: () => void;
}

export default class MobileTourCard extends Component<MobileTourCardAttrs> {
	protected cardAttrs!: MobileTourStepData;

	oninit(vnode: Mithril.Vnode<MobileTourCardAttrs, this>) {
		super.oninit(vnode);

		const { cardId } = this.attrs;

		const communityName = app.forum.attribute<string>('orellthalia_communityName');
		const brandName = app.forum.attribute<string>('orellthalia_brandName');
		const brandDomain = app.forum.attribute<string>('orellthalia_brandDomain');

		const assetDir = this.getAssetDir();

		if (!assetDir) {
			throw new Error('assetDir must be defined in a subclass of MobileTourCard');
		}

		this.cardAttrs = {
			id: cardId,
			image: `${assetDir}/${cardId}.png`,
			title: extractText(app.translator.trans(`glowingblue-orell-thalia-common.forum.tour.home.steps.${cardId}.title`)),
			textShort: extractText(app.translator.trans(`glowingblue-orell-thalia-common.forum.tour.home.steps.${cardId}.text.short`, { communityName })),
			textFull: extractText(
				app.translator.trans(`glowingblue-orell-thalia-common.forum.tour.home.steps.${cardId}.text.full`, { communityName, brandName, brandDomain })
			),
		};

		if (this.cardAttrs.textShort === this.cardAttrs.textFull) {
			this.cardAttrs.textFull = '';
		}
	}

	action() {
		const { id, textFull } = this.cardAttrs;
		if (textFull) {
			app.modal.show(MobileTourStepModal, this.cardAttrs);
		} else if (id === 'restart-tour') {
			this.attrs.complete();
		}
	}
	getAssetDir(): string {
		throw new Error('assetDir getter must be implemented in subclass');
	}
	view(): Mithril.Children {
		const { id, image, title, textShort, textFull } = this.cardAttrs;
		return (
			<div className="MobileTourCard swiper-slide">
				<div className="MobileTourCard-image" style={{ backgroundImage: `url(${image})` }}></div>
				<h3 className="MobileTourCard-title">{m.trust(title)}</h3>
				<div className="MobileTourCard-text" onclick={() => this.action()}>
					<p>{m.trust(textShort)}</p>
					{textFull && (
						<Button className="Button Button--flat MobileTourCard-moreButton">
							{app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.more_label')}
						</Button>
					)}
					{id === 'restart-tour' && (
						<Button className="Button Button--flat MobileTourCard-moreButton">
							{app.translator.trans('glowingblue-orell-thalia-common.forum.tour.home.end_label')}
						</Button>
					)}
				</div>
			</div>
		);
	}
}
